import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChefHat, LogOut, Clock, CheckCircle, AlertCircle, User, WifiOff, Bell, Menu } from 'lucide-react';

import { useAuth, API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { getErrorMessage } from '../utils/errorHandler';
import { useOffline } from '../hooks/useOffline';

const KitchenDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isOffline, cacheData } = useOffline();
  
  // Estados para notificaciones
  const [newOrders, setNewOrders] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const audioRef = useRef(null);
  const previousOrdersRef = useRef([]);
  const soundIntervalRef = useRef(null);
  const isInitialLoadRef = useRef(true); // Para evitar notificación en primera carga

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 5000); // Refresh every 5 seconds
    
    // Marcar como carga inicial completada después del primer render
    setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 100);
    
    return () => clearInterval(interval);
  }, []);

  // Efecto para crear el elemento de audio
  useEffect(() => {
    // Crear un beep sound usando Web Audio API
    const createBeepSound = () => {
      // Verificar si AudioContext está suspendido y resumirlo
      if (audioRef.current && audioRef.current.state === 'suspended') {
        audioRef.current.resume();
      }
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Frecuencia del beep
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    };
    
    // Guardar la función en el ref
    audioRef.current = createBeepSound;
  }, []);

  // Inicializar AudioContext con interacción del usuario
  useEffect(() => {
    const initAudioContext = () => {
      if (!audioRef.current) {
        audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioRef.current.state === 'suspended') {
        audioRef.current.resume();
      }
    };

    // Escuchar clicks para inicializar AudioContext
    document.addEventListener('click', initAudioContext, { once: true });
    document.addEventListener('keydown', initAudioContext, { once: true });
    
    return () => {
      document.removeEventListener('click', initAudioContext);
      document.removeEventListener('keydown', initAudioContext);
    };
  }, []);

  // Detectar nuevas órdenes y reproducir sonido
  useEffect(() => {
    // Si es la primera carga, solo guardar las órdenes sin notificar
    if (isInitialLoadRef.current) {
      previousOrdersRef.current = orders;
      return;
    }
    
    // Si hay órdenes nuevas, buscar diferencias
    if (orders.length > 0) {
      const previousIds = previousOrdersRef.current.map(o => o.id);
      const newOrdersList = orders.filter(o => !previousIds.includes(o.id) && o.status === 'pending');
      
      if (newOrdersList.length > 0) {
        setNewOrders(newOrdersList);
        setShowNotification(true);
        playNotificationSound();
      }
    }
    
    // Actualizar referencia de órdenes anteriores
    previousOrdersRef.current = orders;
  }, [orders]);

  const playNotificationSound = () => {
    // Limpiar cualquier intervalo anterior
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
    }
    
    // Reproducir sonido inmediatamente
    if (audioRef.current) {
      audioRef.current();
    }
    
    // Continuar reproduciendo cada 1.5 segundos
    soundIntervalRef.current = setInterval(() => {
      if (audioRef.current) {
        audioRef.current();
      }
    }, 1500);
  };

  const handleAcknowledgeOrders = () => {
    // Detener el sonido
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    
    setShowNotification(false);
    setNewOrders([]);
  };

  // Limpiar el intervalo al desmontar el componente
  useEffect(() => {
    return () => {
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
      }
    };
  }, []);

  const loadOrders = async () => {
    try {
      // Obtener categorías primero
      const categoriesResponse = await axios.get(`${API}/categories`, {
        headers: getAuthHeaders()
      });
      
      // Cache categories
      await cacheData('categories', categoriesResponse.data);
      
      const allCategories = Array.isArray(categoriesResponse.data) ? categoriesResponse.data : [];
      // Categorías asignadas a OTRA estación (bar, etc.): esos items NO van al tablero de cocina.
      // Todo lo demás (categoría de cocina, item sin categoría o categoría huérfana) SÍ se muestra,
      // para que ningún item quede invisible: si un item no se puede marcar listo, la orden nunca
      // pasa a 'ready' y por tanto nunca llega a delivery (queda trabada en 'in_preparation').
      const otherStationCategoryIds = allCategories
        .filter(cat => cat.print_station && cat.print_station !== 'kitchen')
        .map(cat => cat.id);
      
      // Obtener órdenes: solo la cola activa. Pedir /orders sin filtro de estado
      // convertia la respuesta en una vista de historial, y la ventana de paginacion
      // devolvía las órdenes más antiguas del tenant: las comandas nuevas no aparecían.
      const response = await axios.get(`${API}/orders?status=pending&status=in_preparation&status=ready&limit=200`, {
        headers: getAuthHeaders()
      });
      
      // Filtrar órdenes activas y filtrar items por categoría Y por status del item
      const activeOrders = (Array.isArray(response.data) ? response.data : []).filter(
        (o) => o.status === 'pending' || o.status === 'in_preparation' || o.status === 'ready'
      ).map(order => {
        // Items pendientes/en preparación que NO pertenezcan a otra estación (bar, etc.)
        const kitchenItems = order.items.filter(item => 
          (item.status === 'pending' || item.status === 'in_preparation') &&
          !otherStationCategoryIds.includes(item.category_id)
        );
        
        // Solo incluir orden si tiene items de cocina pendientes/en preparación
        if (kitchenItems.length > 0) {
          return {
            ...order,
            items: kitchenItems,
            original_item_count: order.items.length
          };
        }
        return null;
      }).filter(order => order !== null);
      
      setOrders(activeOrders);
    } catch {
      // If offline, show cached message
      if (isOffline) {
        toast.info('Modo offline - Las órdenes se actualizarán al reconectar', { icon: '📴' });
      } else {
        console.error('Error al cargar órdenes');
      }
    }
  };

  const updateItemStatus = async (orderId, productId, newStatus) => {
    if (isOffline) {
      toast.warning('Sin conexión - Actualización pendiente', {
        description: 'Se sincronizará cuando haya conexión',
        icon: '📴'
      });
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/orders/${orderId}/items/${productId}/status?item_status=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar item'));
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pendiente', className: 'status-pending' },
      in_preparation: { label: 'En Preparación', className: 'status-in-preparation' },
    };
    const statusInfo = statusMap[status] || { label: status, className: '' };
    return <span className={`status-badge ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const inPreparationOrders = orders.filter(o => o.status === 'in_preparation');

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-orange-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-full mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile Menu */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="md:hidden p-2 border-orange-200">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] sm:w-[350px]">
                  <SheetHeader>
                    <SheetTitle>Menú Cocina</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    <div className="pb-4 border-b">
                      <p className="text-sm text-gray-500">Usuario</p>
                      <p className="font-semibold">{user?.name}</p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/profile')}
                      className="w-full justify-start"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Mi Perfil
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={logout}
                      className="w-full justify-start"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Cerrar Sesión
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              
              {/* Offline Indicator */}
              {isOffline && (
                <Badge variant="destructive" className="animate-pulse flex items-center gap-1 text-xs">
                  <WifiOff className="w-3 h-3" />
                  <span className="hidden sm:inline">Offline</span>
                </Badge>
              )}
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <ChefHat className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-gray-800">Cocina</h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">{user?.name}</p>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="hidden md:flex border-orange-200 hover:bg-orange-50 rounded-xl">
                  <User className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Perfil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Salir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-full mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="p-3 sm:p-4 bg-white/80 backdrop-blur-xl border-orange-100">
            <div className="flex items-center gap-2 sm:gap-3">
              <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Pendientes</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-800">{pendingOrders.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white/80 backdrop-blur-xl border-orange-100">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-gray-600">En Preparación</p>
                <p className="text-2xl font-bold text-gray-800">{inPreparationOrders.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white/80 backdrop-blur-xl border-orange-100">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm text-gray-600">Total Activas</p>
                <p className="text-2xl font-bold text-gray-800">{orders.length}</p>
              </div>
            </div>
          </Card>
        </div>

        <h2 className="text-2xl font-bold text-gray-800 mb-6">Órdenes Activas</h2>

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {orders.map((order) => (
            <Card
              key={order.id}
              data-testid={`order-${order.id}`}
              className="p-6 bg-white/80 backdrop-blur-xl border-orange-100 hover-lift"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-1">
                    Orden #{order.order_number}
                  </h3>
                  {getStatusBadge(order.status)}
                </div>
                <div className="text-right text-sm text-gray-600">
                  <p>{order.order_type === 'dine_in' ? 'En Local' : 'Para Llevar'}</p>
                  {order.table_id && <p className="font-semibold">Mesa</p>}
                </div>
              </div>

              {/* Kitchen Items Badge */}
              {order.original_item_count > order.items.length && (
                <div className="mb-3">
                  <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                    🍳 Solo items de cocina ({order.items.length} de {order.original_item_count})
                  </Badge>
                </div>
              )}

              <div className="space-y-2 mb-4">
                {order.items.map((item, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded-lg space-y-2">
                    <div className="flex justify-between items-start text-sm">
                      <div className="flex-1">
                        <span className="text-gray-800 font-medium block">
                          {item.quantity}x {item.product_name}
                        </span>
                        <Badge 
                          className={`mt-1 text-xs ${
                            item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                            'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {item.status === 'pending' ? 'Pendiente' : 'En Preparación'}
                        </Badge>
                      </div>
                      <div className="flex gap-1 ml-2">
                        {item.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => updateItemStatus(order.id, item.item_id || item.product_id, 'in_preparation')}
                            disabled={loading}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 h-7 text-xs"
                          >
                            Iniciar
                          </Button>
                        )}
                        {item.status === 'in_preparation' && (
                          <Button
                            size="sm"
                            onClick={() => updateItemStatus(order.id, item.item_id || item.product_id, 'ready')}
                            disabled={loading}
                            className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 h-7 text-xs"
                          >
                            Listo
                          </Button>
                        )}
                      </div>
                    </div>
                    {item.notes && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                        <p className="text-xs text-gray-700">
                          📝 <strong>Nota:</strong> {item.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {order.notes && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <p className="text-sm text-gray-700">
                    <strong>Notas:</strong> {order.notes}
                  </p>
                </div>
              )}

              {/* Ya no necesitamos botones globales de orden, cada item tiene su control */}
            </Card>
          ))}
        </div>

        {orders.length === 0 && (
          <div className="text-center py-12">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay órdenes activas</p>
          </div>
        )}
      </main>

      {/* Modal de Notificación de Nuevas Órdenes */}
      <Dialog open={showNotification} onOpenChange={() => {}}>
        <DialogContent 
          className="sm:max-w-md bg-gradient-to-br from-orange-500 to-red-500 text-white border-0"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
              <Bell className="w-8 h-8 animate-bounce" />
              ¡Nueva{newOrders.length > 1 ? 's' : ''} Orden{newOrders.length > 1 ? 'es' : ''}!
            </DialogTitle>
            <DialogDescription className="text-white/80">
              Hay nuevas órdenes pendientes en cocina
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 space-y-3">
              {newOrders.map((order, _index) => (
                <div key={order.id} className="bg-white/30 rounded-lg p-3">
                  <p className="text-lg font-bold">Orden #{order.order_number}</p>
                  <p className="text-sm opacity-90">
                    {order.order_type === 'dine_in' ? '🍽️ En Local' : '📦 Para Llevar'}
                  </p>
                  <div className="mt-2 space-y-1">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="space-y-0.5">
                        <p className="text-sm font-medium">
                          • {item.quantity}x {item.product_name}
                        </p>
                        {item.notes && (
                          <p className="text-xs ml-4 bg-yellow-400/30 px-2 py-0.5 rounded">
                            📝 {item.notes}
                          </p>
                        )}
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-sm font-semibold">
                        + {order.items.length - 3} producto(s) más
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={handleAcknowledgeOrders}
              className="w-full bg-white text-orange-600 hover:bg-gray-100 font-bold text-lg py-6 rounded-xl shadow-lg"
            >
              ✓ Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KitchenDashboard;
